import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Data, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { get } from 'lodash-es';

import { BreadcrumbLabels } from 'src/app/constants/breadcrumb-labels.constants';
import { Breadcrumb } from 'src/app/model/breadcrumb.model';
import { StorageConstant } from 'src/app/settings/storage-constants';

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
    private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
    readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

    constructor(private router: Router) {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                const root = this.router.routerState.snapshot.root;
                const breadcrumbs: Breadcrumb[] = [];
                this.addBreadcrumb(root, [], breadcrumbs);
                this._breadcrumbs$.next(breadcrumbs);
            });
    }

    private addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]) {
        if (route) {
            const routeUrl = parentUrl.concat(route.url.map(url => url.path));

            if (route.data.breadcrumb) {
                // Handle parent breadcrumb if specified
                if (route.data.parent) {
                    const previousNav = this.router.getCurrentNavigation()?.previousNavigation;
                    if (previousNav) {
                        const parentTargetRoot = get(
                            previousNav,
                            'targetRouterState.snapshot.root.children',
                        ).filter(x => x.routeConfig?.path === route.data.parent)[0];

                        if (parentTargetRoot) {
                            const breadcrumb = {
                                label: this.getLabel(parentTargetRoot.data, parentTargetRoot),
                                path: '/' + parentTargetRoot.routeConfig.path + '/',
                            };
                            breadcrumbs.push(breadcrumb);
                        }
                    }
                }

                const breadcrumb = {
                    label: this.getLabel(route.data, route),
                    path: '/' + routeUrl.join('/'),
                };
                breadcrumbs.push(breadcrumb);
            }

            this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
        }
    }

    private getLabel(data: Data, route?: ActivatedRouteSnapshot): string {
        const labelKeyOrString = data.breadcrumb;

        // If breadcrumb is a string key you want special logic for:
        const labelMap: Record<string, () => string> = {
            [BreadcrumbLabels.ENTERPRISE_DASHBOARD]: () => this.getEnterpriseDashboardLabel(route),
            [BreadcrumbLabels.AGENT_FILES]: () => this.getAgentFilesLabel(),
            [BreadcrumbLabels.SAFE_TRANSACTIONS_BY_SAFE_ID]: () => this.getSafeTxnsByIdLabel(route),
            [BreadcrumbLabels.SAFE_TRANSACTIONS_BY_SAFE_ID_USER]: () => this.getSafeTxnsBySafeIdUserLabel(route),
            [BreadcrumbLabels.WALLET_BALANCE_SUMMARY]: () => this.getWalletBalanceSummaryLabel(route),

            // add more special cases here
        };

        if (typeof labelKeyOrString === 'string') {
            if (labelMap[labelKeyOrString]) {
                return labelMap[labelKeyOrString]();
            }
            // Otherwise, it's just a plain string label:
            return labelKeyOrString;
        }

        // If breadcrumb is something else (like a function), you can handle or ignore
        // or throw error depending on your app's expectations
        return '';
    }

    private getEnterpriseDashboardLabel(route?: ActivatedRouteSnapshot): string {
        return `${route?.data.businessName ?? 'Enterprise'} Sales`;
    }

    private getWalletBalanceSummaryLabel(route?: ActivatedRouteSnapshot): string {
        return `${route?.data.businessName ?? 'Enterprise'}'s  Balance Summary`;
    }

    private getAgentFilesLabel(): string {
        const user = JSON.parse(localStorage.getItem(StorageConstant.AGENT_FILES));
        return `${user.firstName ?? 'User'} ${user.lastName ?? ''}'s Files`;
    }

    private getSafeTxnsByIdLabel(route?: ActivatedRouteSnapshot): string {
        return `${route?.data.safeId ?? 'Safe ID'}'s transactions`;
    }

    private getSafeTxnsBySafeIdUserLabel(route?: ActivatedRouteSnapshot): string {
        return `${route?.data?.childData?.name ?? 'User'}'s transactions`;
    }
}
